import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { track } from "../analytics";
import { api } from "./client";
import type {
  AdminOverview,
  AdminWorkspaces,
  HistoryChoice,
  IcebreakerRequest,
  IcebreakerResult,
  Me,
  Menuboard,
  MenuboardDraft,
  OrderDetail,
  OrderSummary,
  Person,
  PublicOrder,
  PublicPerson,
  SpeechRequest,
  SpeechResult,
  TabKey,
  Workspace,
} from "./types";

export function useWorkspaces() {
  return useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => (await api.get<Workspace[]>("/workspaces")).data,
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (name: string) => (await api.post<Workspace>("/workspaces", { name })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      // 활성화 1단계. 로그인만 하고 떠난 사람과 여기까지 온 사람을 가른다.
      track("workspace_create");
    },
  });
}

export function useJoinWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (code: string) =>
      (await api.post<Workspace>(`/workspaces/join/${code}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["workspaces"] }),
  });
}

export function usePeople(workspaceId: string) {
  return useQuery({
    queryKey: ["people", workspaceId],
    queryFn: async () => (await api.get<Person[]>(`/workspaces/${workspaceId}/people`)).data,
    enabled: Boolean(workspaceId),
  });
}

export function useAddPeople(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (names: string) =>
      (await api.post<Person[]>(`/workspaces/${workspaceId}/people`, { names })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people", workspaceId] }),
  });
}

export function useSetPersonActive(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { personId: string; active: boolean }) =>
      (
        await api.patch<Person>(`/workspaces/${workspaceId}/people/${input.personId}`, {
          active: input.active,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["people", workspaceId] }),
  });
}

export function useSeedMenuboards() {
  return useQuery({
    queryKey: ["seed-menuboards"],
    queryFn: async () => (await api.get<Menuboard[]>("/menuboards/seeds")).data,
  });
}

export function useMenuboards(workspaceId: string) {
  return useQuery({
    queryKey: ["menuboards", workspaceId],
    queryFn: async () => (await api.get<Menuboard[]>(`/workspaces/${workspaceId}/menuboards`)).data,
    enabled: Boolean(workspaceId),
  });
}

export function useImportMenuboard(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (seedId: string) =>
      (await api.post<Menuboard>(`/workspaces/${workspaceId}/menuboards/import/${seedId}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menuboards", workspaceId] }),
  });
}

export function useCreateMenuboard(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (draft: MenuboardDraft) =>
      (await api.post<Menuboard>(`/workspaces/${workspaceId}/menuboards`, draft)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menuboards", workspaceId] }),
  });
}

/** 사진 → 저장되지 않은 초안. 사용자가 확인·수정한 뒤 useCreateMenuboard 로 저장한다. */
export function useExtractMenuboard() {
  return useMutation({
    mutationFn: async (photo: File) => {
      const form = new FormData();
      form.append("photo", photo);
      // Content-Type 은 axios 가 boundary 까지 붙여 만들게 둔다 — 직접 지정하면
      // boundary 가 빠져 FastAPI 가 파일을 못 찾는다.
      return (await api.post<MenuboardDraft>("/menuboards/extract", form)).data;
    },
  });
}

export function useUpdateMenuboard(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (menuboard: Menuboard) =>
      (
        await api.put<Menuboard>(`/workspaces/${workspaceId}/menuboards/${menuboard.id}`, {
          name: menuboard.name,
          menus: menuboard.menus,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["menuboards", workspaceId] }),
  });
}

export function useOrders(workspaceId: string) {
  return useQuery({
    queryKey: ["orders", workspaceId],
    queryFn: async () => (await api.get<OrderSummary[]>(`/workspaces/${workspaceId}/orders`)).data,
    enabled: Boolean(workspaceId),
  });
}

export function useOrder(workspaceId: string, orderId: string) {
  return useQuery({
    queryKey: ["order", workspaceId, orderId],
    queryFn: async () =>
      (await api.get<OrderDetail>(`/workspaces/${workspaceId}/orders/${orderId}`)).data,
    enabled: Boolean(workspaceId && orderId),
  });
}

export function useCreateOrder(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { menuboard_id: string; person_ids: string[] }) =>
      (await api.post<OrderSummary>(`/workspaces/${workspaceId}/orders`, input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", workspaceId] });
      // 핵심 전환. GA4 콘솔에서 이 이벤트만 '주요 이벤트'로 표시한다.
      track("order_create");
    },
  });
}

export function useCloseOrder(workspaceId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) =>
      (await api.post<OrderSummary>(`/workspaces/${workspaceId}/orders/${orderId}/close`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["orders", workspaceId] });
      qc.invalidateQueries({ queryKey: ["order", workspaceId] });
    },
  });
}

/** 건배사·n행시 생성. 저장하지 않으니 무효화할 쿼리도 없다 — 결과는 이 mutation 이
 *  들고 있고(`data`), 모드를 바꿀 때 `reset()` 으로 버린다. */
export function useGenerateSpeech() {
  return useMutation({
    mutationFn: async (input: SpeechRequest) =>
      (await api.post<SpeechResult>("/toast/generate", input)).data,
  });
}

/** 내기를 한 번 돌렸다고 서버에 알린다. 뽑기 자체는 브라우저에서 끝나고 결과는
 *  보내지 않는다 — 모드와 인원 수만 간다.
 *
 *  실패는 삼킨다. 지표는 곁가지지 기능이 아니다(`PUT /users/me` 와 같은 원칙) —
 *  네트워크가 죽었다고 이미 뽑아 놓은 결과를 못 보게 만들면 안 된다. */
export function reportDrawUsage(input: { mode: "lots" | "ladder"; size: number }): void {
  void api.post("/draw/usage", input).catch(() => undefined);
}

/** 어드민 여부만 확인하는 값싼 호출. 403 이면 무거운 집계를 부르지 않는다.
 *  권한 없음은 재시도해도 결과가 같으니 retry 를 끈다. */
export function useAdminMe() {
  return useQuery({
    queryKey: ["admin-me"],
    queryFn: async () => (await api.get<{ email: string }>("/admin/me")).data,
    retry: false,
    // 세션 중에 어드민 여부가 바뀌지 않는다. 무한 staleTime 이 없으면 창을 옮길
    // 때마다 재조회가 돌아 일반 사용자에게 403 이 반복해서 찍힌다.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAdminOverview(days: number, enabled: boolean) {
  return useQuery({
    queryKey: ["admin-metrics", "overview", days],
    queryFn: async () =>
      (await api.get<AdminOverview>("/admin/metrics/overview", { params: { days } })).data,
    enabled,
    // 기간을 바꿔도 화면이 비워지지 않게 이전 기간 값을 그대로 들고 있는다.
    placeholderData: keepPreviousData,
    // 한 번 부를 때마다 서버가 컬렉션 다섯 개를 통째로 읽는다. 기본값이면 탭을
    // 다시 볼 때마다 전수 스캔이 두 번 돈다.
    staleTime: 60_000,
  });
}

export function useAdminWorkspaces(days: number, enabled: boolean) {
  return useQuery({
    queryKey: ["admin-metrics", "workspaces", days],
    queryFn: async () =>
      (await api.get<AdminWorkspaces>("/admin/metrics/workspaces", { params: { days } })).data,
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function usePublicOrder(slug: string) {
  return useQuery({
    queryKey: ["public-order", slug],
    queryFn: async () => (await api.get<PublicOrder>(`/public/orders/${slug}`)).data,
  });
}

export function usePublicPeople(slug: string) {
  return useQuery({
    queryKey: ["public-people", slug],
    queryFn: async () => (await api.get<PublicPerson[]>(`/public/orders/${slug}/people`)).data,
  });
}

export function usePersonHistory(slug: string, personId: string | null) {
  return useQuery({
    queryKey: ["history", slug, personId],
    queryFn: async () =>
      (await api.get<HistoryChoice[]>(`/public/orders/${slug}/people/${personId}/history`)).data,
    enabled: Boolean(personId),
  });
}

export function useSubmit(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      person_id: string;
      // menu_id 와 custom_menu 중 하나만 채운다 — 둘 다 오면 서버가 400 을 준다.
      menu_id: string;
      custom_menu: string;
      choice_ids: string[];
      note: string;
    }) => (await api.post(`/public/orders/${slug}/submissions`, input)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["public-people", slug] });
      qc.invalidateQueries({ queryKey: ["history", slug] });
      // 링크를 받은 바깥 사람이 실제로 쓴 지점. 로그인이 없어 서버가 사용자를
      // 셀 수 없는 유일한 화면이라 여기만은 GA 가 유일한 눈이다.
      track("submission_complete");
    },
  });
}

/** 대화 주제 생성. 건배사와 같다 — 저장하지 않으니 무효화할 쿼리도 없고, 결과는 이
 *  mutation 이 들고 있다. */
export function useGenerateIcebreaker() {
  return useMutation({
    mutationFn: async (input: IcebreakerRequest) =>
      (await api.post<IcebreakerResult>("/icebreaker/generate", input)).data,
  });
}

/** 내 정보. 지금 쓰는 건 탭 노출 설정뿐이다.
 *
 *  `AppShell` 이 모든 화면에서 이걸 읽는다 — 실패하면 감춘 탭이 없는 것으로 본다
 *  (`hidden_tabs` 기본값이 빈 목록이라 설정을 못 읽었을 때 화면이 비지 않는다). */
export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => (await api.get<Me>("/users/me")).data,
    retry: false,
  });
}

export function useUpdateMySettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (hidden_tabs: TabKey[]) =>
      (await api.put<Me>("/users/me/settings", { hidden_tabs })).data,
    // 응답이 저장된 설정 전체라 그대로 캐시에 심는다 — 재조회 한 번을 아끼고,
    // 탭 줄이 저장 직후 바로 바뀐다.
    onSuccess: (me) => qc.setQueryData(["me"], me),
  });
}
